import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Subject, combineLatest, map, of, switchMap, takeUntil } from 'rxjs';

import { ContextTool, ContextToolbarService } from './context-toolbar.service';

interface ContextLauncherItem {
  tool: ContextTool;
  disabled: boolean;
  icon: string;
  warning: boolean;
  warningMessage: string;
}

@Component({
  selector: 'app-context-launcher',
  templateUrl: './context-launcher.component.html',
  styleUrls: ['./context-launcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContextLauncherComponent implements OnDestroy {
  items: ContextLauncherItem[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly actionIcons: Record<string, string> = {
    Back: 'pi pi-arrow-left',
    Delete: 'pi pi-trash',
    Duplicate: 'pi pi-copy',
    Edit: 'pi pi-pencil',
    Export: 'pi pi-download',
    Publish: 'pi pi-send',
    Reset: 'pi pi-refresh',
    'Reset tags': 'pi pi-refresh',
  };

  constructor(
    private readonly contextToolbarService: ContextToolbarService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.contextToolbarService.currentTools
      .pipe(
        switchMap((tools) =>
          tools.length
            ? combineLatest(
                tools.map((tool) =>
                  combineLatest([tool.visible$, tool.disabled$, tool.warning$, tool.warningMessage$]).pipe(
                    map(([visible, disabled, warning, warningMessage]) => ({ tool, visible, disabled, warning, warningMessage })),
                  ),
                ),
              )
            : of([]),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((toolStates) => {
        this.items = toolStates
          .filter(({ visible }) => visible)
          .map(({ tool, disabled, warning, warningMessage }) => ({
            tool,
            disabled,
            warning,
            warningMessage,
            icon: this.actionIcons[tool.caption] ?? 'pi pi-wrench',
          }));
        this.changeDetectorRef.markForCheck();
      });
  }

  executeAction(item: ContextLauncherItem): void {
    if (!item.disabled) {
      item.tool.clickHandler();
    }
  }

  get availableCount(): number {
    return this.items.length;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
